"use client";

import { useState } from "react";

function readSanityToken(projectId: string | undefined): string | null {
  if (typeof window === "undefined") return null;
  try {
    const specific = projectId ? window.localStorage.getItem(`__studio_auth_token_${projectId}`) : null;
    if (specific) {
      const parsed = JSON.parse(specific) as { token?: string };
      if (parsed?.token) return parsed.token;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("__studio_auth_token_")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { token?: string };
      if (parsed?.token) return parsed.token;
    }
  } catch {
    // ignore
  }
  return null;
}

export function SeedDefaultsButton() {
  const [status, setStatus] = useState<"idle" | "running" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  const seed = async () => {
    if (!confirm("Fill empty Sanity fields with default content? Existing values are preserved.")) {
      return;
    }

    setStatus("running");
    setMessage(null);
    const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
    const token = readSanityToken(projectId);
    if (!token) {
      setStatus("error");
      setMessage("No Studio session found — open /studio first.");
      return;
    }

    try {
      const response = await fetch("/api/admin/seed-defaults", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = (await response.json().catch(() => null)) as
        | { totalFilled?: number; error?: string }
        | null;
      if (!response.ok) {
        setStatus("error");
        setMessage(data?.error ?? "Seed failed.");
        return;
      }
      setStatus("done");
      setMessage(`Filled ${data?.totalFilled ?? 0} field${data?.totalFilled === 1 ? "" : "s"}.`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error("[Owner] Seed failed", error);
      setStatus("error");
      setMessage("Seed failed. Try again.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={seed}
        disabled={status === "running"}
        className="rounded-sm border border-white/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] transition hover:bg-white hover:text-slate-900 disabled:opacity-60 sm:px-3 sm:tracking-[0.3em]"
        title="Fill empty Sanity fields with hardcoded defaults so they become editable"
      >
        {status === "running" ? "…" : (
          <>
            <span className="sm:hidden">Seed</span>
            <span className="hidden sm:inline">Seed defaults</span>
          </>
        )}
      </button>
      {message ? (
        <span className="text-[10px] normal-case tracking-normal text-white/80">{message}</span>
      ) : null}
    </>
  );
}
